import React, { useState } from 'react';
import { Chip, Stack } from '@mui/material';
import { Form, FormSection, SectionTitle, SectionDescription, PhoneNumberField } from '../../Components/Form';
import { P21CustomerMatch } from '../../Types/customerAccounts';
import P21CustomerPicker from './P21CustomerPicker';

export interface NewCustomerAccount {
  phone: string;
  p21CustomerIds: string[];
}

/**
 * Add an SMS-login customer: the person's mobile number and the P21 customers they may see.
 * abatix.com customers and AIMS workers get their accounts automatically and are not added here.
 */
const CustomerAccountForm: React.FC<{
  onSubmit: (input: NewCustomerAccount) => Promise<void>;
  onCancel: () => void;
}> = ({ onSubmit, onCancel }) => {
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [customers, setCustomers] = useState<P21CustomerMatch[]>([]);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone || phoneError || !customers.length) return;
    setSaving(true);
    try {
      await onSubmit({ phone, p21CustomerIds: customers.map((c) => c.customerId) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form
      embedded
      hideTitle
      onSubmit={handleSubmit}
      subtitle="For customers who sign in with an SMS code. abatix.com customers and AIMS workers get their accounts automatically."
      submitLabel="Add Customer"
      onCancel={onCancel}
      isLoading={saving}
      disableSubmit={!phone || !!phoneError || !customers.length || saving}
    >
      <FormSection>
        <SectionTitle>Mobile number</SectionTitle>
        <SectionDescription>The sign-in code is texted to this number. It must not belong to another account.</SectionDescription>
        <PhoneNumberField value={phone} onChange={setPhone} onValidationChange={setPhoneError} />
      </FormSection>

      <FormSection>
        <SectionTitle>P21 customers</SectionTitle>
        <SectionDescription>The person sees orders for every customer listed here. Add one per site they manage.</SectionDescription>
        <P21CustomerPicker
          value={null}
          exclude={customers.map((c) => c.customerId)}
          onChange={(customer) => customer && setCustomers((prev) => [...prev, customer])}
        />
        {customers.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {customers.map((c) => (
              <Chip
                key={c.customerId}
                label={`${c.customerId} · ${c.customerName ?? 'Unnamed customer'}`}
                onDelete={() => setCustomers((prev) => prev.filter((p) => p.customerId !== c.customerId))}
              />
            ))}
          </Stack>
        )}
      </FormSection>
    </Form>
  );
};

export default CustomerAccountForm;
